/**
 * @lines98/client/services/identity — long-lived player identity (T-6 client).
 *
 * Per ADR-0006 §D1 Pipeline Stage 1:
 *   - On boot, load identity from IndexedDB (key `lines98.player-id.v1`).
 *   - If absent, POST /auth/anonymous → server mints {playerId, secretToken,
 *     createdAt, isTest} → save to IndexedDB.
 *   - All subsequent api-client calls attach `Authorization: Bearer <secretToken>`
 *     via api-client.buildHeaders().
 *
 * Storage: IndexedDB (long-lived; survives частичную очистку cookies, cross-tab
 * shared per origin). Falls back to localStorage если IndexedDB unavailable
 * (private browsing на старых браузерах). idb-keyval library is ~600 bytes
 * (per CONSTRAINTS bundle budget).
 *
 * In-memory cache:
 *   - After boot, `_current` holds the active identity. `getCurrent()` /
 *     `getCurrentBearer()` are SYNCHRONOUS reads for hot paths (api-client headers).
 *   - `update()` writes both memory + IndexedDB (OAuth callback rotates secretToken
 *     → must persist before next request).
 *
 * Cross-tab sync:
 *   - Two tabs share IndexedDB but each maintains its own in-memory cache. Если
 *     tab A logs in (rotates token), tab B's `getCurrentBearer()` returns the OLD
 *     token until A's next requests succeed под new token. To minimize this
 *     window we listen для `storage` events (sentinel пишется в localStorage с
 *     каждым update) и re-read IndexedDB on signal.
 *
 * Cross-references:
 *   - ADR-0006 §D1 (Pipeline Stage 1: anonymous mint at boot).
 *   - ADR-0006 §D2 (Bearer transport — getCurrentBearer feeds api-client).
 *   - ADR-0006 §D7 (OAuth callback rotates secretToken — calls update()).
 *   - api-client.ts (consumer for Bearer).
 *   - services/replay-store.ts (sibling pattern for client-side persistence).
 */

import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

import { apiClient } from './api-client.js';

// =============================================================================
// Types
// =============================================================================

/** Способ, которым игрок залогинен (для строки-статуса профиля §2.5). */
export type AuthMethod = 'email' | 'google' | 'discord';

export interface IdentityRecord {
  readonly playerId: string;
  readonly secretToken: string;
  /** ISO 8601 — first_seen_at от server response. */
  readonly createdAt: string;
  readonly isTest: boolean;
  /**
   * Публичный ник (server-truth displayName), если задан (email-аккаунт). null —
   * аноним / OAuth без ника. Персистится, чтобы профиль показал ник сразу при
   * перезаходе без сетевого запроса (ADR-0006 rev3 §3; spec §2.5). НЕ PII.
   */
  readonly displayName?: string | null;
  /**
   * Способ входа (§2.5 label). Персистится для строки-статуса «Вход по почте /
   * Google / Discord». null/undefined — аноним (вход не выполнялся).
   */
  readonly authMethod?: AuthMethod | null;
  /**
   * Замаскированная почта `a•••@gmail.com` (Q-3) — ТОЛЬКО masked-форма, для
   * напоминания «какой почтой вошёл» на СВОЁМ профиле. Вычисляется клиентом из
   * введённой при login/register почты (полная почта НЕ хранится — spec §4 / I-3);
   * на своём устройстве masked-строка допустима. null — OAuth/аноним (почты нет).
   */
  readonly maskedEmail?: string | null;
  /** Schema version — v1 (legacy без displayName/authMethod) или v2. */
  readonly v: 1 | 2;
}

/**
 * Server response shape for POST /auth/anonymous. Aligned with
 * packages/server/src/routes/auth/anonymous.ts:successResponseSchema.
 */
interface AnonymousMintResponse {
  playerId: string;
  secretToken: string;
  createdAt: string;
  isTest: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const IDB_KEY = 'lines98.player-id.v1';
/** Sentinel key in localStorage that fires `storage` events on update (cross-tab). */
const STORAGE_EVENT_KEY = 'lines98.identity-updated-at.v1';

// =============================================================================
// Internal state
// =============================================================================

let _current: IdentityRecord | null = null;
let _bootPromise: Promise<IdentityRecord | null> | null = null;
let _storageListenerInstalled = false;

// =============================================================================
// IndexedDB helpers (defensive)
// =============================================================================

async function loadFromStorage(): Promise<IdentityRecord | null> {
  try {
    const raw = (await idbGet(IDB_KEY)) as unknown;
    if (isIdentityRecord(raw)) return raw;
    return null;
  } catch {
    // IndexedDB unavailable (private browsing in some browsers, disabled).
    // localStorage fallback:
    try {
      const fallback = window.localStorage?.getItem(IDB_KEY);
      if (!fallback) return null;
      const parsed = JSON.parse(fallback) as unknown;
      return isIdentityRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}

async function saveToStorage(record: IdentityRecord): Promise<void> {
  try {
    await idbSet(IDB_KEY, record);
  } catch {
    // IndexedDB write fail — try localStorage fallback.
    try {
      window.localStorage?.setItem(IDB_KEY, JSON.stringify(record));
    } catch {
      // Both failed; in-memory only — non-fatal for current session.
    }
  }
  // Bump sentinel to notify other tabs.
  try {
    window.localStorage?.setItem(STORAGE_EVENT_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

async function deleteFromStorage(): Promise<void> {
  try {
    await idbDel(IDB_KEY);
  } catch {
    // ignore
  }
  try {
    window.localStorage?.removeItem(IDB_KEY);
  } catch {
    // ignore
  }
}

// =============================================================================
// Cross-tab sync
// =============================================================================

function installStorageListener(): void {
  if (_storageListenerInstalled) return;
  if (typeof window === 'undefined') return;
  _storageListenerInstalled = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_EVENT_KEY) return;
    // Another tab updated identity — refresh memory cache from IndexedDB.
    void loadFromStorage().then((rec) => {
      if (rec) _current = rec;
    });
  });
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Boot the identity service. Idempotent — multiple calls share the same promise.
 *
 * 1. Try to load existing identity from IndexedDB.
 * 2. If absent, POST /auth/anonymous → save server-minted identity.
 * 3. Populate in-memory cache.
 *
 * Returns the active identity or null if mint failed (network / 5xx). On null,
 * callers should retry on next user action; app continues in degraded mode
 * (local-only play, no server features).
 */
export function bootIdentity(): Promise<IdentityRecord | null> {
  if (_bootPromise) return _bootPromise;
  _bootPromise = (async (): Promise<IdentityRecord | null> => {
    try {
      installStorageListener();

      const existing = await loadFromStorage();
      if (existing) {
        _current = existing;
        return existing;
      }

      // Mint new anonymous identity. No Bearer attached (api-client.buildHeaders
      // omits header если _current is null).
      const response = await apiClient.post<AnonymousMintResponse>(
        '/auth/anonymous',
        {},
      );
      if (!response.ok) {
        // Server unreachable / 5xx — leave _current null. Reset bootPromise so
        // caller can retry next time.
        _bootPromise = null;
        return null;
      }

      const minted: IdentityRecord = {
        v: 1,
        playerId: response.body.playerId,
        secretToken: response.body.secretToken,
        createdAt: response.body.createdAt,
        isTest: response.body.isTest,
      };
      _current = minted;
      await saveToStorage(minted);
      return minted;
    } catch {
      // Network throw (ApiClientError 'network') or storage failure. Reset
      // bootPromise so caller can retry next time — без этого следующие
      // вызовы bootIdentity() возвращают тот же rejected promise навсегда.
      // Code-reviewer MEDIUM #1, 2026-05-25.
      _bootPromise = null;
      return null;
    }
  })();
  return _bootPromise;
}

/**
 * Get current identity (synchronous — reads in-memory cache).
 * Returns null if bootIdentity() hasn't completed OR mint failed.
 */
export function getCurrent(): IdentityRecord | null {
  return _current;
}

/**
 * Get current Bearer secretToken (synchronous — for api-client header injection).
 * Returns null if no identity yet.
 */
export function getCurrentBearer(): string | null {
  return _current?.secretToken ?? null;
}

/**
 * Был ли текущий игрок АВТОРИЗОВАН (вошёл через email / Google / Discord), а не
 * анонимный mint. Признак — `authMethod` (выставляется ТОЛЬКО на успешном
 * login/register: local-auth → 'email', oauth-callback → 'google'/'discord';
 * аноним — null/undefined). Синхронно (in-memory cache).
 *
 * Используется api-client'ом, чтобы отличить «сессия отозвана в другом браузере»
 * (authed → 401 token_not_recognized = плашка, НЕ тихий re-mint) от обычного
 * stale-анонима / пересозданной БД (anon → тихий re-mint = поведение
 * fresh-browser). Корень инцидента 2026-06-10 — оба случая раньше re-минтились
 * молча, тихий kick авторизованного создавал призрачного анонима.
 */
export function isAuthed(): boolean {
  return _current?.authMethod != null;
}

/**
 * Update identity (e.g., after OAuth callback rotates secretToken и/или
 * resolves к different playerId per subsequent-login flow).
 *
 * Writes through to IndexedDB + bumps cross-tab sentinel.
 */
export async function update(
  partial: Pick<IdentityRecord, 'playerId' | 'secretToken'> &
    Partial<
      Pick<
        IdentityRecord,
        'createdAt' | 'isTest' | 'displayName' | 'authMethod' | 'maskedEmail'
      >
    >,
): Promise<IdentityRecord> {
  const existing = _current;
  const next: IdentityRecord = {
    v: 2,
    playerId: partial.playerId,
    secretToken: partial.secretToken,
    createdAt: partial.createdAt ?? existing?.createdAt ?? new Date().toISOString(),
    isTest: partial.isTest ?? existing?.isTest ?? false,
    // displayName/authMethod/maskedEmail: explicit value wins; else carry the
    // existing record's (so a secretToken-only rotation keeps the known nick/method).
    // A different playerId (subsequent-login to another account) passes fresh values.
    displayName:
      partial.displayName !== undefined ? partial.displayName : existing?.displayName ?? null,
    authMethod:
      partial.authMethod !== undefined ? partial.authMethod : existing?.authMethod ?? null,
    maskedEmail:
      partial.maskedEmail !== undefined ? partial.maskedEmail : existing?.maskedEmail ?? null,
  };
  _current = next;
  await saveToStorage(next);
  return next;
}

/**
 * Clear identity from memory + storage. Mostly для testing / debug.
 * Production users НЕ should hit this (next mint creates new anonymous playerId
 * — теряется ВСЯ прежняя история, не recoverable).
 */
export async function clear(): Promise<void> {
  _current = null;
  _bootPromise = null;
  await deleteFromStorage();
}

// =============================================================================
// Test seam: in-memory state reset (testing only — not exported as default API)
// =============================================================================

/**
 * Reset in-memory state for tests (does NOT touch storage). Tests typically
 * mock fetch + reset memory before each case.
 */
export function _resetForTests(): void {
  _current = null;
  _bootPromise = null;
  _storageListenerInstalled = false;
}

/** Test seam: directly seed in-memory identity (skips boot flow). */
export function _setForTests(rec: IdentityRecord | null): void {
  _current = rec;
}

// =============================================================================
// Type guard
// =============================================================================

function isIdentityRecord(value: unknown): value is IdentityRecord {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (v['v'] === 1 || v['v'] === 2) && // v1 (legacy, no nick/method) + v2 forward-compat
    typeof v['playerId'] === 'string' &&
    typeof v['secretToken'] === 'string' &&
    typeof v['createdAt'] === 'string' &&
    typeof v['isTest'] === 'boolean'
  );
}
